import CreateEventDroppable, { CreateEventDroppableProps } from './CreateEventDroppable';
import EventDropHintView from './EventDropHintView';
import { observer } from 'mobx-react-lite';
import { addMinutes, OwaDate } from 'owa-datetime';
import { DraggableItemTypes } from 'owa-dnd';
import { ConditionalDropHint } from 'owa-drophint-views';
import * as React from 'react';
import {
    getEventDropState,
    getTimePanelDropState,
    isHoveringOverDayDropHint,
    getDayViewDropTimeMinuteOffset,
} from '../selectors/timePanelDndStoreSelectors';
import {
    setShowDayDropHint,
    setDayViewDropTimeMinuteOffset,
} from '../actions/timePanelDndStoreActions';

const styles = require('./TimePanelDayDropHintView.scss');
const classNames = require('classnames');

export type TimePanelDayDropHintViewProps = {
    children?: React.ReactNode;
} & TimePanelDayInitialDropHintProps &
    TimePanelDayPreviewDroppableProps;

const HOVER_WAIT_TIME = 500;

export default observer(function TimePanelDayDropHintView(props: TimePanelDayDropHintViewProps) {
    const { children } = props;
    const timePanelDropState = getTimePanelDropState();

    return (
        <ConditionalDropHint
            dragItemType={DraggableItemTypes.MailListRow}
            dropViewState={timePanelDropState}
            hideDropHintCondition={isHoveringOverDayDropHint()}
            dropHintView={<TimePanelDayInitialDropHint {...props} />}>
            <TimePanelDayPreviewDroppable {...props}>{children}</TimePanelDayPreviewDroppable>
        </ConditionalDropHint>
    );
});

type TimePanelDayInitialDropHintProps = {
    containerClassName: string;
} & Pick<CreateEventDroppableProps, 'selectedDate'>;

function TimePanelDayInitialDropHint(props: TimePanelDayInitialDropHintProps) {
    const { selectedDate, containerClassName } = props;
    const eventDropState = getEventDropState();

    return (
        <CreateEventDroppable
            dropViewState={eventDropState}
            dropTarget={'DayViewDropHint'}
            selectedDate={selectedDate}
            classNames={classNames(containerClassName, styles.timePanelDayDropHintView)}
            handleDragEnter={handleDragEnter}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}>
            <EventDropHintView dropViewState={eventDropState} />
        </CreateEventDroppable>
    );
}

type TimePanelDayPreviewDroppableProps = {
    containerClassName: string;
    children?: React.ReactNode;
} & Pick<CreateEventDroppableProps, 'selectedDate'>;

function TimePanelDayPreviewDroppable(props: TimePanelDayPreviewDroppableProps) {
    const { selectedDate, containerClassName, children } = props;
    const eventDropState = getEventDropState();

    return (
        <CreateEventDroppable
            dropViewState={eventDropState}
            dropTarget={'DayViewPreviewDropHint'}
            selectedDate={selectedDate}
            generateStartTime={generateStartTimeForDayPreview}
            classNames={containerClassName}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}>
            {children}
        </CreateEventDroppable>
    );
}

// avoid flickering UI when user drags over and then leaves before timer fires
let dropHintTimerId: number | undefined = undefined;

function handleDragEnter() {
    if (!isHoveringOverDayDropHint()) {
        dropHintTimerId = window.setTimeout(() => {
            setShowDayDropHint(true);
        }, HOVER_WAIT_TIME);
    }
}

function handleDragLeave() {
    if (dropHintTimerId) {
        window.clearTimeout(dropHintTimerId);
    }
    setShowDayDropHint(false);
}

function handleDrop() {
    setShowDayDropHint(false);
    setDayViewDropTimeMinuteOffset(null);
}

function generateStartTimeForDayPreview(selectedDate?: OwaDate) {
    return selectedDate && addMinutes(selectedDate, getDayViewDropTimeMinuteOffset());
}
